import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { NAV_ITEMS, PHONE_TEL, WA_LINK } from "../../lib/site";
import { useLang } from "../../lib/i18n";
import { LangToggle } from "./LangToggle";
import { LogoMark, LogoLockup } from "./Logo";

export const Header = () => {
    const { t } = useLang();
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goMobile = (e, path) => {
        e.preventDefault();
        setOpen(false);
        setTimeout(() => navigate(path), 250);
    };

    const navClass = (isActive) =>
        `text-sm font-medium transition-colors duration-300 ${
            isActive ? "text-amber-400" : "text-slate-300 hover:text-amber-300"
        }`;

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled
                        ? "border-b border-white/10 bg-night-950/85 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="container-x flex h-[68px] items-center justify-between">
                    <Link
                        to="/"
                        data-testid="header-logo"
                        className="group flex items-center gap-3"
                    >
                        <LogoMark />
                        <LogoLockup />
                    </Link>

                    <nav data-testid="desktop-nav" className="hidden items-center gap-7 lg:flex">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                end={item.path === "/"}
                                data-testid={`nav-link-${item.key}`}
                                className={({ isActive }) => navClass(isActive)}
                            >
                                {t.nav[item.key]}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <LangToggle className="hidden lg:flex" idSuffix="desktop" />
                        <Link
                            to="/boeken"
                            data-testid="header-cta-book"
                            className="btn-amber hidden px-5 py-2.5 text-sm sm:inline-flex"
                        >
                            {t.nav.bookCta}
                        </Link>
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="mobile-menu-open"
                            aria-label="Menu openen"
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
                        >
                            <Menu size={20} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[60] flex flex-col bg-night-950/97 backdrop-blur-2xl lg:hidden"
                    >
                        <div className="container-x flex h-[68px] items-center justify-between">
                            <span className="flex items-center gap-3">
                                <LogoMark />
                                <LogoLockup />
                            </span>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="mobile-menu-close"
                                aria-label="Menu sluiten"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <nav className="container-x mt-8 flex flex-col gap-1">
                            {NAV_ITEMS.map((item, i) => (
                                <motion.a
                                    key={item.path}
                                    href={item.path}
                                    onClick={(e) => goMobile(e, item.path)}
                                    data-testid={`mobile-nav-link-${item.key}`}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                    className="flex items-baseline gap-4 border-b border-white/5 py-4"
                                >
                                    <span className="font-mono text-xs text-amber-400/70">
                                        0{i + 1}
                                    </span>
                                    <span className="font-display font-wide text-2xl font-bold uppercase text-white">
                                        {t.nav[item.key]}
                                    </span>
                                </motion.a>
                            ))}
                        </nav>
                        <div className="container-x mt-auto pb-28">
                            <div className="mb-4 flex justify-center">
                                <LangToggle idSuffix="mobile" />
                            </div>
                            <div className="flex gap-3">
                                <a href={PHONE_TEL} data-testid="mobile-menu-call" className="btn-amber flex-1 px-4 py-3.5 text-sm">
                                    <Phone size={16} /> {t.nav.call}
                                </a>
                                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp" className="btn-outline flex-1 px-4 py-3.5 text-sm">
                                    <MessageCircle size={16} /> {t.nav.whatsapp}
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
